/**
 * EKSPORT — kontrakten for hele plattformen (docs/EKSPORT.md).
 *
 * Én definisjon per eksport. Det som står her er DEN DELEN AV DEFINISJONEN BEGGE SIDER TRENGER:
 * navn, deler, kolonner, formater og hvor den kan startes fra. Dialogen i nettleseren bygges av
 * den, og API-et sjekker forespørselen mot den. Hvordan dataene hentes og settes opp, står i
 * API-ets halvdel (`apps/api/src/export/definitions/`), med samme `id`.
 *
 * PDF, DOCX og CSV lages fra den samme definisjonen og de samme dataene. Ingen eksport har egen
 * meny, egen mal eller eget filformat.
 */
import { z } from 'zod';
import { pageCursorFields } from '../http/pagination.js';

import type { Action } from '../policy/types.js';
import type { ExportRule } from './rules.js';

export const EXPORT_FORMATS = ['pdf', 'docx', 'csv'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];
export const exportFormatSchema = z.enum(EXPORT_FORMATS).meta({ id: 'ExportFormat' });

/**
 * `document`: ett dokument per objekt (en advance, et artistkort, et dagsark). Delene velges, og
 * mange objekter blir én samlet fil eller én fil per objekt.
 * `table`: én tabell (en liste). Kolonnene velges, og PDF/DOCX er tabellen satt opp på papir.
 */
export const EXPORT_KINDS = ['document', 'table'] as const;
export type ExportKind = (typeof EXPORT_KINDS)[number];

/**
 * Hvor eksporten startes fra (EKSPORT.md §3):
 * `object` ett objekt · `selection` merkede rader · `filter` alle som passer filteret ·
 * `parent` alt under nivået over (alle advancene i lineupen, hele vaktplanen).
 */
export const EXPORT_SCOPE_KINDS = ['object', 'selection', 'filter', 'parent'] as const;
export type ExportScopeKind = (typeof EXPORT_SCOPE_KINDS)[number];

/** Hvordan en verdi skrives. Formateringen ligger ett sted (API-ets `render/format.ts`). */
export const EXPORT_COLUMN_TYPES = [
  'text',
  /** Lengre tekst med linjeskift. */
  'multiline',
  'email',
  /** Alltid tekst i CSV, så Excel ikke spiser nuller og plusstegn. */
  'phone',
  /** Alltid tekst i CSV, så «0150» ikke blir 150. */
  'postcode',
  /** `YYYY-MM-DD` inn, dd.mm.åååå ut. */
  'date',
  /** ISO-tidspunkt inn, i organisasjonens tidssone ut. */
  'datetime',
  /** `HH:MM` inn. */
  'time',
  'integer',
  /** Desimaltall, komma som desimaltegn på norsk. */
  'decimal',
  /** `{ minor, currency }` inn. */
  'money',
  'boolean',
  /** Ferdig oversatt statustekst (definisjonen slår opp `enums.*`). */
  'status',
] as const;
export type ExportColumnType = (typeof EXPORT_COLUMN_TYPES)[number];

/** Områdene i katalogen (EKSPORT.md §9). Hvert område har sin egen fil i `catalogue/`. */
export const EXPORT_DOMAINS = [
  'festival',
  'booking',
  'agency',
  'venue',
  'concerts',
  'common',
  'settings',
  'control_room',
  'artist',
] as const;
export type ExportDomain = (typeof EXPORT_DOMAINS)[number];

export interface ExportSectionMeta {
  /** Stabil nøkkel: `travel`, `stay`. Lagres i brukerens siste valg. */
  readonly key: string;
  /** Språknøkkel i `nb.json`/`en.json` (grenen `export.`). */
  readonly labelKey: string;
  /**
   * Handlinger som alle må være lov for at delen skal finnes. Mangler én, vises den ikke i
   * dialogen og tas aldri med — økonomi og kontrakt er bare med for den som ser dem fra før.
   */
  readonly requires?: readonly Action[];
  /** En navngitt regel som også må holde (250.19, `exportRuleAllows`) — for det `requires` ikke kan si. */
  readonly rule?: ExportRule;
  /** Ikke krysset av som standard. Standard er krysset av (EKSPORT.md §5). */
  readonly defaultOff?: boolean;
}

export interface ExportColumnMeta {
  readonly key: string;
  readonly labelKey: string;
  readonly type: ExportColumnType;
  readonly requires?: readonly Action[];
  /** En navngitt regel som også må holde (250.19): beløpskolonnene på bookingen. */
  readonly rule?: ExportRule;
  /** Ikke med som standard; brukeren kan legge den til. */
  readonly defaultHidden?: boolean;
}

/**
 * ET VALG I DIALOGEN som ikke er en del eller en kolonne: «Ta med frivillige» på kjøkkenlista
 * (persontypene §1). Sendes som en boolsk verdi i `params[key]`. Uten krysset er det av.
 */
export interface ExportOptionMeta {
  readonly key: string;
  readonly labelKey: string;
  /** Handlinger som alle må være lov for at valget skal vises og virke. */
  readonly requires?: readonly Action[];
}

export interface ExportMeta {
  /** `<område>.<navn>`, f.eks. `festival.advance`. Små bokstaver, punktum og bindestrek. */
  readonly id: string;
  readonly domain: ExportDomain;
  readonly kind: ExportKind;
  /** Språknøkkel for navnet: «Advance», «Hotelliste». */
  readonly titleKey: string;
  readonly formats: readonly ExportFormat[];
  readonly scopes: readonly ExportScopeKind[];
  /**
   * Handlingene skjermen krever for å vise det samme. Sjekkes med `can()` i tillegg til
   * eksportcella (`export:create`). Dataene hentes uansett gjennom de samme tjenestene og RLS.
   */
  readonly requires: readonly Action[];
  /** Eksterne med innlogging kan bruke den, innenfor det som er delt med dem. */
  readonly guest: boolean;
  /** Bare `document`: delene man krysser av for. */
  readonly sections?: readonly ExportSectionMeta[];
  /** Bare `table`: kolonnene. Rekkefølgen her er standardrekkefølgen. */
  readonly columns?: readonly ExportColumnMeta[];
  /**
   * Bare `table`: nøkkelen til tabellen på skjermen (`Table tableKey`). Da følger eksporten
   * kolonnene brukeren har valgt der, så lenge ingen kolonner er valgt i dialogen.
   */
  readonly tableKey?: string;
  /**
   * Parametre forespørselen MÅ ha i `params` (f.eks. `editionId`). Dialogen sender dem fra siden;
   * API-et avviser forespørselen uten dem.
   */
  readonly params?: readonly string[];
  /** A4 liggende for brede tabeller. */
  readonly landscape?: boolean;
  /** Valg i dialogen, sendt som boolske `params` (`ExportOptionMeta`). */
  readonly options?: readonly ExportOptionMeta[];
}

// ─── Forespørselen ──────────────────────────────────────────────────────────────────────────────

export const MAX_EXPORT_SELECTION = 1000;

const scalarParam = z.union([z.string().max(500), z.number(), z.boolean()]);
const paramValue = z.union([scalarParam, z.array(z.string().max(200)).max(200)]);

export const exportScopeSchema = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('object'), id: z.uuid() }),
    z.object({
      kind: z.literal('selection'),
      ids: z.array(z.uuid()).min(1).max(MAX_EXPORT_SELECTION),
    }),
    /**
     * Filteret er det samme spørreobjektet lista på skjermen sender til sitt eget endepunkt
     * (uten `limit` og `cursor`). Definisjonen validerer det med listas eget skjema.
     */
    z.object({ kind: z.literal('filter'), filter: z.record(z.string(), paramValue) }),
    z.object({ kind: z.literal('parent'), id: z.uuid() }),
  ])
  .meta({ id: 'ExportScope' });
export type ExportScope = z.infer<typeof exportScopeSchema>;

export const EXPORT_BUNDLES = ['single', 'perObject'] as const;
export type ExportBundle = (typeof EXPORT_BUNDLES)[number];

export const createExportRequestSchema = z
  .object({
    exportId: z
      .string()
      .regex(/^[a-z_]+\.[a-z0-9-]+$/)
      .max(80),
    formats: z
      .array(exportFormatSchema)
      .min(1)
      .max(EXPORT_FORMATS.length)
      .refine((list) => new Set(list).size === list.length, { message: 'duplicate_format' }),
    scope: exportScopeSchema,
    /** Sidens sammenheng: `editionId`, `festivalId`, `from`/`to` … */
    params: z.record(z.string(), paramValue).default({}),
    /** `document`: valgte deler. Utelatt betyr alle brukeren har tilgang til. */
    sections: z.array(z.string().max(60)).max(60).optional(),
    /** `table`: valgte kolonner i rekkefølge. Utelatt betyr standard. */
    columns: z.array(z.string().max(60)).max(80).optional(),
    /** `document` med flere objekter: én samlet fil, eller én fil per objekt. */
    bundle: z.enum(EXPORT_BUNDLES).default('single'),
  })
  .meta({ id: 'CreateExportRequest' });
export type CreateExportRequest = z.input<typeof createExportRequestSchema>;
export type ParsedExportRequest = z.output<typeof createExportRequestSchema>;

// ─── Jobben ─────────────────────────────────────────────────────────────────────────────────────

export const EXPORT_JOB_STATUSES = ['running', 'ready', 'failed', 'expired'] as const;
export type ExportJobStatus = (typeof EXPORT_JOB_STATUSES)[number];

/**
 * Svaret når eksporten er stor: `202` med jobben. En liten eksport svarer `200` med selve fila,
 * og jobben er da bare en rad i historikken.
 */
export const exportJobDtoSchema = z
  .object({
    id: z.uuid(),
    exportId: z.string(),
    status: z.enum(EXPORT_JOB_STATUSES),
    formats: z.array(exportFormatSchema),
    fileName: z.string().nullable(),
    sizeBytes: z.number().int().nullable(),
    /** Antall objekter eller rader i eksporten. */
    itemCount: z.number().int(),
    createdAt: z.iso.datetime({ offset: true }),
    finishedAt: z.iso.datetime({ offset: true }).nullable(),
    /** Når fila slettes fra lageret (7 dager). `null` for små eksporter som ble lastet ned med en gang. */
    expiresAt: z.iso.datetime({ offset: true }).nullable(),
    /** Fila ligger i lageret og kan lastes ned. */
    downloadable: z.boolean(),
    errorCode: z.string().nullable(),
  })
  .meta({ id: 'ExportJobDto' });
export type ExportJobDto = z.infer<typeof exportJobDtoSchema>;

/** `GET /v1/exports`: mine eksporter i organisasjonen de siste sju dagene, nyeste først. */
export const exportJobListResponseSchema = z
  .object({ items: z.array(exportJobDtoSchema), ...pageCursorFields })
  .meta({ id: 'ExportJobListResponse' });
export type ExportJobListResponse = z.infer<typeof exportJobListResponseSchema>;

/** Hvor lenge en ferdig fil ligger i lageret (EKSPORT.md §8). */
export const EXPORT_RETENTION_DAYS = 7;

/**
 * Brukerens siste valg per eksport, lagret med `/v1/me/view-prefs/:viewKey`.
 * Nøkkelen er `export-<id med bindestrek>:<organisasjon>`, f.eks. `export-festival-advance:<uuid>`.
 */
export function exportViewKey(exportId: string, organizationId: string): string {
  return `export-${exportId.replace(/[._]/g, '-')}:${organizationId}`;
}

export interface ExportChoice {
  readonly formats: readonly ExportFormat[];
  readonly sections?: readonly string[];
  readonly columns?: readonly string[];
  readonly bundle?: ExportBundle;
}
